import { memo, useRef, useState } from 'react';
import cls from 'classnames';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingFocusManager,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react';

import IconLensF11 from '@/assets/icons/icon-lens-f-11.svg?react';
import IconLensF16 from '@/assets/icons/icon-lens-f-16.svg?react';
import IconLensF28 from '@/assets/icons/icon-lens-f-28.svg?react';
import IconLensFInfinity from '@/assets/icons/icon-lens-f-infinity.svg?react';
import { LENS_F_VALUES, LensFValue } from '@/constants/lensFValues';

import styles from './LensFNumberButton.module.scss';

interface LensFNumberButtonProps {
  lensFNumber: LensFValue;
  onSelectFNumber: (lensFNumber: LensFValue) => void;
}

function LensFNumberButton(props: LensFNumberButtonProps) {
  const { lensFNumber, onSelectFNumber } = props;

  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating({
    placement: 'left-end',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(10),
      flip({ fallbackAxisSideDirection: 'end' }),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const headingId = useId();

  function onSelectFNumberHandler(lensFNumber: LensFValue) {
    setIsOpen(false);

    onSelectFNumber(lensFNumber);
  }

  return (
    <>
      <button type="button" aria-label="Change lens F number" ref={refs.setReference} {...getReferenceProps()}>
        {lensFNumber === LensFValue.F11 && <IconLensF11 width="25" height="25" />}

        {lensFNumber === LensFValue.F16 && <IconLensF16 width="25" height="25" />}

        {lensFNumber === LensFValue.F28 && <IconLensF28 width="25" height="25" />}

        {lensFNumber === LensFValue.FInfinity && <IconLensFInfinity width="25" height="25" />}
      </button>

      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div className={styles.selection} ref={refs.setFloating} style={floatingStyles} aria-labelledby={headingId} {...getFloatingProps()}>
            <div className={styles.label}>Aperture</div>

            <div className={styles.buttons}>
              {LENS_F_VALUES.map((value) => (
                <button
                  key={value}
                  className={cls(styles.button, { [styles.active]: lensFNumber === value })}
                  aria-label={`Set lens F number ${value}`}
                  onClick={() => onSelectFNumberHandler(value)}
                >
                  <span>f/</span>

                  {value === -1 && <span>&infin;</span>}

                  {value !== -1 && <span>{value}</span>}
                </button>
              ))}
            </div>

            <FloatingArrow ref={arrowRef} context={context} className={styles.arrow} />
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
}

export default memo(LensFNumberButton);
